import { useQueryClient } from '@tanstack/react-query'
import {
  bffCrmPersonInsurance,
  providersUserPeople,
  BffCrmPersonInsuranceCreateBodyParams,
  BffCrmPersonInsuranceCreateResponse,
  BffCrmPersonInsuranceRetrieveResponse,
  insuranceMember,
  InsuranceMemberPartialUpdateBodyParams,
  InsuranceMemberPartialUpdateResponse,
  userCoverageSetStatus,
  UserCoverageSetStatusCreateBodyParams,
  UserCoverageSetStatusCreateResponse,
  UserCoverageStatusesListResponse,
} from '@fireflyhealth/api'

import { useMutation, useQuery } from '~/shared/hooks/query'
// Edit Insurance writes now route through the CRM BFF (`bff/crm/person/{person_id}/insurance/`),
// which is keyed by `person_id` instead of the legacy `insurance_member_info.id`.
// The address-only path (`useCreateOrUpdateInsuranceAddress`) below still PATCHes
// `/insurance/member/{id}` because the address form predates this BFF endpoint.
// `BffCrmPersonInsuranceCreateBodyParams` already declares every field optional
// (the BFF applies a partial update — see `api/firefly/bff/crm/person_insurance.py`),
// so the generated body type doubles as the editable surface here with no
// `Partial<>` wrapper or cast at the call site.
type CreateOrUpdateInsurancePayload = {
  personId: number
} & BffCrmPersonInsuranceCreateBodyParams

export interface PatientInsuranceAddressPayload {
  insuranceInfo: {
    id: number
    addresses?: NonNullable<InsuranceMemberPartialUpdateBodyParams['addresses']>
  }
}

async function createOrUpdateInsurance(
  payload: CreateOrUpdateInsurancePayload,
): Promise<BffCrmPersonInsuranceCreateResponse> {
  const { personId, ...body } = payload
  const { data } = await bffCrmPersonInsurance.create({ person_id: personId }, { data: body })
  return data
}

export const useCreateOrUpdateInsurance = () => {
  const client = useQueryClient()
  return useMutation(
    createOrUpdateInsurance,
    {
      onSuccess: async (_data, variables) => {
        // Invalidate the narrow BFF insurance query for any component that
        // calls `useGetPersonInsurance` directly (e.g., a future read-only widget).
        await client.invalidateQueries({
          queryKey: bffCrmPersonInsurance.retrieveQueryKey({ person_id: variables.personId }),
        })
        // Invalidate the full person query so `PersonDetail` (which calls
        // `useGetPerson`) refetches the canonical person row — including the
        // complete `insurance_info` shape (with `insurance_plan.networks`) that
        // `EditPerson` needs. This is the loader half of the loader/action pair:
        //   loader  → useGetPerson  (GET /providers/me/user/people/{id}/)
        //   action  → useCreateOrUpdateInsurance  (POST /bff/crm/person/{id}/insurance/)
        //   connect → invalidate person query → PersonDetail re-renders → EditPerson
        //             receives updated props → Formik enableReinitialize picks up
        //             the persisted insurance row without a manual page refresh.
        await client.invalidateQueries({
          queryKey: providersUserPeople.retrieveQueryKey({
            id: variables.personId,
            provider_id: 'me',
          }),
        })
      },
    },
    { success: 'Patient Insurance info updated', error: 'Failed to update patient Insurance' },
  )
}

export const useGetPersonInsurance = (
  variables: { personId: number },
  options: { enabled?: boolean } = {},
) => {
  // Loader half of the CRM Edit Insurance loader/action pair. Pairs with
  // `useCreateOrUpdateInsurance` above (action) and is invalidated by it.
  // `enabled` lets callers skip the fetch when the parent already knows the
  // person has no `insurance_info` row, since the BFF responds 404 in that
  // case and the error would surface as a toast.
  const pathParams = { person_id: variables.personId }
  return useQuery<BffCrmPersonInsuranceRetrieveResponse>(
    bffCrmPersonInsurance.retrieveQueryKey(pathParams),
    async () => (await bffCrmPersonInsurance.retrieve(pathParams)).data,
    { enabled: options.enabled },
    { error: 'Failed to load insurance info' },
  )
}

async function createOrUpdateInsuranceAddress(
  payload: PatientInsuranceAddressPayload,
): Promise<InsuranceMemberPartialUpdateResponse> {
  const { id, addresses } = payload.insuranceInfo
  const { data } = await insuranceMember.partialUpdate({ id }, { data: { addresses } })
  return data
}

export const useCreateOrUpdateInsuranceAddress = () => {
  return useMutation(
    createOrUpdateInsuranceAddress,
    {
      onSuccess: async () => {},
    },
    { success: 'Patient Insurance info updated', error: 'Failed to update patient Insurance' },
  )
}

interface PatientInsuranceCoveragePayload {
  patientId: number
  coverage: UserCoverageStatusesListResponse[number]
}

// The generated `userCoverageSetStatus` endpoint validates the body against a
// strict {status, reason?} schema, so extract just those keys from the full
// coverage entry the caller already has in hand.
async function updateInsuranceCoverage(
  payload: PatientInsuranceCoveragePayload,
): Promise<UserCoverageSetStatusCreateResponse> {
  const { patientId, coverage } = payload
  const body: UserCoverageSetStatusCreateBodyParams = {
    status: coverage.status,
    reason: coverage.reason,
  }
  const { data } = await userCoverageSetStatus.create({ id: patientId }, { data: body })
  return data
}

export const useUpdateInsuranceCoverage = () => {
  return useMutation(
    updateInsuranceCoverage,
    {
      onSuccess: async () => {},
    },
    {
      success: 'Patient insurance coverage status updated',
      error: 'Problem updating patient insurance coverage status',
    },
  )
}
